/*
 * Copyright (c) 2026 SimplyBook.me Ltd. All rights reserved.
 * Licensed under the Apache License, Version 2.0.
 */

import React, { useState } from "react";
import { htmlToPlainText } from "../../../utils/sanitize";
import { servicesRequireMembership } from "../../../utils/membershipGrant";
import Title from "../../ui/Title";
import Text from "../../ui/Text";
import Flex from "../../ui/Flex";
import Icon from "../../ui/Icon";
import T from "../../ui/Translation";
import {
  MembershipBadge,
  MetaGroup,
  ServiceDescriptionModal,
  ServiceFooter,
  ServiceItemButton,
  ServiceItemInfo,
  ServiceItemInfoHeader,
  ServiceItemInfoInner,
  ServiceItemInner,
  ServiceMedia,
  ServicePricing,
  StyledServiceItem,
} from "../service/ServiceItemParts";
import { GroupBookingCounterView } from "../../GroupBookingCounter";
import { useAssetsSrc } from "../../../hooks/useAssetsSrc";
import usePluginUtils from "../../../hooks/usePluginUtils";
import { useBooking } from "../../../hooks/useBooking";
import { useClassSelection } from "../../../hooks/useClassSelection";
import { useStepManager } from "../../../hooks/useStepManager";
import { getClassStartRoute } from "../../../utils/classes";

const Capacity = ({ capacity }) => (
  <Flex $align="center" $gap="0.25rem">
    <Icon.Users color="secondary" />
    <Text>
      {capacity > 1 ? <T capacity={capacity}>up to %capacity%</T> : capacity}
    </Text>
  </Flex>
);

const ClassItem = ({ classItem, category }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { membershipAccessGrants } = useBooking();
  const { isPluginEnabled } = usePluginUtils();
  const { selectedClass, selectClass } = useClassSelection();
  const { navigateToStep } = useStepManager();
  const image = useAssetsSrc(classItem.picture_preview);

  const isSelected = selectedClass?.id === classItem.id;
  const minSeats = classItem.min_group_booking || 1;
  const [seats, setSeats] = useState(isSelected ? selectedClass.group_booking_count || minSeats : minSeats);
  const showCounter = isPluginEnabled("group_booking") && classItem.limit_booking > 1;
  const requiresMembership = isPluginEnabled("membership") && servicesRequireMembership([classItem]);
  const membershipApplied =
    requiresMembership && isSelected && membershipAccessGrants?.[classItem.id]?.clientMembershipId != null;

  const handleClick = () => {
    if (selectClass(classItem, category, showCounter ? seats : 1)) {
      navigateToStep(getClassStartRoute(classItem, isPluginEnabled("paid_attributes")));
    }
  };

  const handleReadMore = (e) => {
    e?.stopPropagation();
    setIsModalOpen(true);
  };

  return (
    <>
      <StyledServiceItem selected={isSelected} onClick={handleClick}>
        <ServiceMedia image={image} name={classItem.name} placeholder={<Icon.ClassPlaceholderIcon size={70} />} />

        <ServiceItemInner>
          <ServiceItemInfo $align="start" $justify="space-between" $gap="1rem">
            <ServiceItemInfoInner $gap="0.125rem" $align="start">
              <ServiceItemInfoHeader $gap="0.5rem" $align="center" $justify="space-between" $wrap="wrap">
                <Title size="h3">{classItem.name}</Title>
                {requiresMembership && <MembershipBadge applied={membershipApplied} />}
              </ServiceItemInfoHeader>
              {classItem.description && (
                <Text size="small" lineClamp={2} onReadMoreClick={handleReadMore}>
                  {htmlToPlainText(classItem.description)}
                </Text>
              )}
            </ServiceItemInfoInner>

            <ServiceItemButton
              variant="outline"
              size="icon"
              icon={<Icon.ChevronRight />}
              tabIndex={-1}
              aria-hidden={true}
            />
          </ServiceItemInfo>

          <ServiceFooter $justify="space-between" $align="baseline" $gap="0.5rem">
            <ServicePricing service={classItem} />

            <MetaGroup $gap="0.25rem" $align="end">
              {showCounter ? (
                <GroupBookingCounterView
                  counter={seats}
                  canDecrease={seats > minSeats}
                  canIncrease={seats < classItem.limit_booking}
                  onDecrease={() => setSeats(seats - 1)}
                  onIncrease={() => setSeats(seats + 1)}
                />
              ) : (
                <Capacity capacity={classItem.provider.qty} />
              )}
            </MetaGroup>
          </ServiceFooter>
        </ServiceItemInner>
      </StyledServiceItem>

      <ServiceDescriptionModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        service={classItem}
        image={image}
      />
    </>
  );
};

export default ClassItem;
